/**
 * Minimal promise-based key/value cache over IndexedDB.
 *
 * IndexedDB (unlike localStorage) stores Blobs natively and holds hundreds of
 * MB, which is what the offline floorplan-image and device-catalog caches need.
 *
 * Values are stored via the structured clone algorithm, so any cloneable value
 * works (objects, arrays, Blobs, ArrayBuffers). Each logical cache is a separate
 * object store inside one shared database.
 *
 * Degrades gracefully: in environments without IndexedDB (very old browsers,
 * some private-mode configs) every operation becomes a safe no-op — get()
 * resolves to undefined and set()/delete() resolve without error, so callers
 * transparently fall back to the network.
 */

const DB_NAME = 'hts-survey-cache';
// Bumped whenever the set of known stores changes so onupgradeneeded runs.
const DB_VERSION = 7;

// All object stores must be declared up front — IndexedDB only allows store
// creation inside a version-change transaction. Add new caches here (and bump
// DB_VERSION) rather than creating stores on demand.
const KNOWN_STORES = ['floorplans', 'devices', 'pending-floorplans', 'photos', 'photo-outbox', 'map-tiles', 'asset-bytes', 'pending-p2p'] as const;

export type IdbStoreName = (typeof KNOWN_STORES)[number];

export interface IdbCache {
  get<T>(key: string): Promise<T | undefined>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  keys(): Promise<string[]>;
  clear(): Promise<void>;
}

function hasIndexedDb(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        for (const store of KNOWN_STORES) {
          if (!db.objectStoreNames.contains(store)) {
            db.createObjectStore(store);
          }
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

const NOOP_CACHE: IdbCache = {
  get: async () => undefined,
  set: async () => {},
  delete: async () => {},
  keys: async () => [],
  clear: async () => {},
};

export function openIdbCache(storeName: IdbStoreName): IdbCache {
  if (!hasIndexedDb()) return NOOP_CACHE;

  async function withStore<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await openDb();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = fn(tx.objectStore(storeName));
      let result: T;
      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () => reject(request.error);
      // Resolve on transaction commit, not merely request success: a write is
      // not durable or visible to a later transaction until the transaction
      // completes, so awaiting oncomplete avoids cross-transaction read races.
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  return {
    async get<T>(key: string): Promise<T | undefined> {
      const value = await withStore<T>('readonly', (s) => s.get(key));
      return value ?? undefined;
    },
    async set<T>(key: string, value: T): Promise<void> {
      await withStore('readwrite', (s) => s.put(value as unknown as never, key));
    },
    async delete(key: string): Promise<void> {
      await withStore('readwrite', (s) => s.delete(key));
    },
    async keys(): Promise<string[]> {
      const keys = await withStore<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
      return keys.map((k) => String(k));
    },
    async clear(): Promise<void> {
      await withStore('readwrite', (s) => s.clear());
    },
  };
}

/**
 * Evict every cached store — used on sign-out so customer floorplans, photos,
 * and the device catalog don't persist on a shared/returned iPad for the next
 * user. Best-effort and never throws: clearing one store's failure doesn't
 * block the others, and a missing IndexedDB is a no-op. Covers all
 * {@link KNOWN_STORES} automatically, so new caches are wiped without touching
 * this call site.
 */
export async function clearAllIdbCaches(): Promise<void> {
  if (!hasIndexedDb()) return;
  await Promise.all(
    KNOWN_STORES.map(async (store) => {
      try {
        await openIdbCache(store).clear();
      } catch {
        /* best-effort per-store eviction */
      }
    }),
  );
}
